import React, { ChangeEvent, forwardRef, useId } from 'react';

import { cn } from '~/utils/styles';

interface TextFieldProps {
  label?: string;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number';
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  id?: string;
  /** For E2E tests */
  'data-testid'?: string;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      value,
      onChange,
      placeholder,
      type = 'text',
      className,
      inputClassName,
      labelClassName,
      required = false,
      disabled = false,
      error = false,
      errorMessage,
      id,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id || `textfield-${generatedId.replace(/:/g, '')}`;

    return (
      <div className={cn('flex flex-col space-y-2', className)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'text-sm font-medium text-gray-700',
              required && 'after:ml-0.5 after:text-red-500 after:content-["*"]',
              disabled && 'text-gray-400',
              labelClassName,
            )}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          data-testid={dataTestId}
          className={cn(
            'custom-input-focus flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-red-500',
            inputClassName,
          )}
          {...props}
        />
        {error && errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
      </div>
    );
  },
);

TextField.displayName = 'TextField';

export { TextField };
